import type { ClaimStatusKey, CommissionItem, CommissionTimelineView, EventKey, UiCommissionStatus } from "@mfa-aff/shared";
import { type CommissionTimelineData, type ExplainLine, REASONS, addDays, daysUntil, formatPercentBp, formatToman, isReasonCode } from "@mfa-aff/ui";
import type { CommissionChannelFilter } from "../../api/orders";

type Tone = "brand" | "success" | "gray" | "warning" | "error";

/** Badge tone and a one-line «why» per UI status; the label itself comes with each item (`statusLabel`). */
export const COMMISSION_STATUS: Record<UiCommissionStatus, { label: string; why: string; color: Tone }> = {
    pending: { label: "در دورهٔ ضمانت", why: "مشتری تا پایان ۱۴ روز ضمانت می‌تواند وجهش را پس بگیرد؛ بعد از آن خودکار تأیید می‌شود.", color: "brand" },
    available: { label: "قابل برداشت", why: "به موجودی قابل برداشت شما اضافه شده و هر وقت بخواهید برداشت می‌شود.", color: "success" },
    paid_out: { label: "پرداخت‌شده", why: "به کیف پول یا حساب بانکی شما واریز شده است.", color: "gray" },
    held: { label: "در حال بررسی", why: "برای بررسی بیشتر نگه داشته شده است.", color: "warning" },
    reversed: { label: "برگشت خورد", why: "به‌خاطر بازگشت وجه به مشتری برگشت خورده است.", color: "error" },
    void: { label: "بدون پورسانت", why: "این سفارش پورسانتی برای شما ندارد.", color: "gray" },
};

/** Channel filter options (the API's `channel` query, plus `renewal`). */
export const CHANNEL_LABEL: Record<CommissionChannelFilter, string> = {
    link: "لینک",
    code: "کد تخفیف",
    refcode: "کد معرف",
    campaign: "کمپین",
    manual: "تخصیص دستی",
    renewal: "تمدید",
};

export const EVENT_LABEL: Record<EventKey, string> = {
    new: "فروش جدید",
    renewal: "تمدید",
    upgrade: "ارتقا",
    addon: "افزودنی",
};

/**
 * «برندهٔ انتساب»: which row of the priority table (design doc §6.1) decided this order, in words.
 * The other partner is never named.
 */
export function winningRule(c: Pick<CommissionItem, "ruleStep" | "ruleStepLabel" | "reasonCode">): string {
    const s = c.ruleStep;
    const step = s === null ? "" : ` (گام ${s})`;
    if (c.reasonCode === "ATTRIBUTED_TO_OTHER_CODE") return `کد تخفیف بازاریاب دیگر${step}`;
    if (c.reasonCode === "BOUND_TO_OTHER_AFFILIATE") return `مشتری متصل به بازاریاب دیگر${step}`;
    if (c.ruleStepLabel) return `${c.ruleStepLabel}${step}`;
    return s === null ? "نامشخص" : `گام ${s}`;
}

/** Calculation lines from the server's `explain`, including a proportional clawback when there is one. */
export function commissionLines(c: CommissionItem): { lines: ExplainLine[]; sentence: string } {
    const e = c.explain;
    const clawed = BigInt(e.clawedBackRial) > 0n;
    const lines: ExplainLine[] = [
        { label: "مبلغ مشمول (بدون مالیات و تخفیف فاکتور)", value: formatToman(e.baseRial) },
        { op: "×", label: e.rateLabel, value: formatPercentBp(e.rateBp) },
    ];
    if (e.discountBp > 0) lines.push({ op: "−", label: "تخفیفی که شما به مشتری دادید", value: formatPercentBp(e.discountBp) });
    lines.push({ op: "=", label: "پورسانت شما", value: formatToman(e.resultRial), emphasis: !clawed });
    if (clawed) {
        lines.push(
            { op: "−", label: "بازپس‌گیری متناسب پس از بازپرداخت", value: formatToman(e.clawedBackRial) },
            { op: "=", label: "مانده برای شما", value: formatToman(e.netRial), emphasis: true },
        );
    }
    return { lines, sentence: e.sentence };
}

/** Footnote for the explain popover: rate-card version and any rate adjustments. */
export const explainFootnote = (c: CommissionItem): string =>
    [
        "محاسبه با نرخ همان روز پرداخت انجام شده و تغییر نرخ‌های بعدی روی آن اثری ندارد.",
        c.explain.ruleVersionLabel ? `(${c.explain.ruleVersionLabel})` : "",
        ...c.explain.adjustments,
    ]
        .filter(Boolean)
        .join(" ");

/** The flat fields the UI `CommissionTimeline` component takes (branch reason as a reason code). */
export function timelineData(t: CommissionTimelineView): CommissionTimelineData {
    const { steps: _s, currentStep: _c, summary: _m, branch, ...flat } = t;
    return branch ? { ...flat, branch: { ...branch, reason: isReasonCode(branch.reason) ? branch.reason : "UNDER_REVIEW" } } : flat;
}

/** Last day a claim can be filed for this order (`claimDays` from `GET claims`). */
export const claimDeadline = (c: CommissionItem, claimDays: number) => addDays(c.timeline.orderedAt, claimDays);

/** Whether a claim makes sense and can still be filed for this commission. */
export const canClaim = (c: CommissionItem, claimDays: number) =>
    !!c.reasonCode && isReasonCode(c.reasonCode) && REASONS[c.reasonCode].claimable && daysUntil(claimDeadline(c, claimDays)) > 0;

/** WHMCS service status (as the API sends it) → badge tone and a one-line «why». */
export function serviceStatus(status: string | null): { why: string; color: Tone } {
    switch (status) {
        case "Active":
            return { why: "با هر تمدید، پورسانت تمدید به شما می‌رسد.", color: "success" };
        case "Suspended":
            return { why: "فاکتور تمدید پرداخت نشده؛ اگر مشتری پرداخت کند، سرویس و پورسانت تمدیدش برمی‌گردد.", color: "warning" };
        case "Terminated":
        case "Cancelled":
            return { why: "مشتری سرویس را لغو کرده و تمدیدی در کار نیست.", color: "gray" };
        case "Fraud":
            return { why: "سرویس در بررسی امنیتی نامعتبر شناخته شد.", color: "error" };
        default:
            return { why: "وضعیت سرویس هنوز از میزبان‌فا دریافت نشده است.", color: "gray" };
    }
}

/** WHMCS billing cycle → Persian; unknown values are shown as sent. */
export function cycleLabel(cycle: string | null): string | null {
    if (!cycle) return null;
    const map: Record<string, string> = {
        monthly: "ماهانه",
        quarterly: "سه‌ماهه",
        "semi-annually": "شش‌ماهه",
        semiannually: "شش‌ماهه",
        annually: "سالانه",
        biennially: "دوساله",
        triennially: "سه‌ساله",
        "one time": "یک‌باره",
        "free account": "رایگان",
    };
    return map[cycle.toLowerCase()] ?? cycle;
}

export const CLAIM_STATUS: Record<ClaimStatusKey, { color: Tone }> = {
    open: { color: "brand" },
    approved: { color: "success" },
    rejected: { color: "error" },
};
